import { Center } from '@starryui10/layouts/Center';
import { Spinner } from '@starryui10/Loading/Spinner';
import { color } from '@starryui10/theme/color';
import * as React from 'react';
import styled from 'styled-components';
import { DisplayImage } from './DisplayImage';
import { UpdatePhotoOverlay } from './UpdatePhotoOverlay';
import { buildFontSize, buildShape, buildSize } from '../utils';

export interface IThumbnailProps extends avatar.CommonThumbnailProps {
    onUploadClick?: () => void;
    onClick?: () => void;

    placeholder: (props: avatar.PlaceholderProps) => React.ReactElement;

    shape: avatar.ThumbnailShape;
    showLoading?: boolean;
    showUpload?: boolean;
}

type ThumbnailState = 'Loading' | 'ShowImage' | 'ShowAlternative';

export const Thumbnail = React.forwardRef<HTMLDivElement, IThumbnailProps>(
    (
        {
            showLoading = true,
            role = 'presentation',
            altText,
            size = 'Normal',
            shape,
            src = '',
            placeholder,
            showUpload = false,
            onUploadClick = () => {
                /* Empty */
            },
            onClick = () => {
                /* Empty */
            },
        },
        uploadPhotoRef
    ) => {
        const [state, setState] = React.useState<ThumbnailState>(src && showLoading ? 'Loading' : 'ShowAlternative');
        const [isHoveredOrFocused, setIsHoveredOrFocused] = React.useState(false);

        const wrapperDivRef = React.createRef<HTMLDivElement>();

        const actualSize = buildSize(size);
        const actualShape = buildShape(shape);
        const actualFontSize = buildFontSize(size);

        const handleImageLoadSuccess = () => {
            setState('ShowImage');
        };

        const handleImageLoadFail = () => {
            setState('ShowAlternative');
        };

        const handleClick = () => {
            if (onClick) {
                onClick()
            }
        };

        const handleOnKeyPress = (e: React.KeyboardEvent<HTMLDivElement>) => {
            const target = e.target as HTMLElement;
            if (e.nativeEvent.code === 'Space') {
                target.click();
            }
        };

        return (
            <>
                <WrapperDiv
                    ref={wrapperDivRef}
                    FontSize={actualFontSize}
                    BorderRadius={actualShape}
                    Size={`${actualSize}px`}
                    onClick={handleClick}
                    IsClickable={!!onClick}
                    onKeyPress={handleOnKeyPress}
                    onMouseEnter={showUpload ? () => setIsHoveredOrFocused(true) : undefined}
                    onMouseLeave={showUpload ? () => setIsHoveredOrFocused(false) : undefined}
                    onFocus={showUpload ? () => setIsHoveredOrFocused(true) : undefined}
                    onBlur={showUpload ? () => setIsHoveredOrFocused(false) : undefined}
                    tabIndex={showUpload ? undefined : 0}
                    role={showUpload ? undefined : 'button'}
                >
                    <DisplayImage
                        showImage={state === 'ShowImage'}
                        altText={altText}
                        src={src}
                        role={role}
                        onLoad={handleImageLoadSuccess}
                        onError={handleImageLoadFail}
                    />

                    {state === 'Loading' && (
                        <LoaderContainer>
                            <Spinner size={actualFontSize} />
                        </LoaderContainer>
                    )}

                    {state === 'ShowAlternative' && <Center>{placeholder({ size: actualSize })}</Center>}

                    {showUpload && (
                        <UpdatePhotoOverlay
                            ref={uploadPhotoRef}
                            isFullSize={state === 'ShowAlternative'}
                            actualSize={actualSize}
                            size={size}
                            show={isHoveredOrFocused}
                            onUploadClick={onUploadClick}
                        />
                    )}
                </WrapperDiv>
            </>
        );
    }
);

interface IImageProps {
    Size: string;
    BorderRadius: string;
    FontSize: string;
    IsClickable: boolean;
}

const WrapperDiv = styled.div<IImageProps>`
    height: ${(props) => props.Size};
    width: ${(props) => props.Size};
    border-radius: ${(props) => props.BorderRadius};
    overflow: hidden;
    background-color: ${color.Blue.B60};
    color: white;
    font-size: ${(props) => props.FontSize};
    position: relative;
    cursor: ${(props) => (props.IsClickable ? 'pointer' : 'inherit')};
    display: inline-block;
`;

const LoaderContainer = styled.div`
    justify-content: center;
    align-self: center;
    display: flex;
    width: 100%;
`;
